import { FluentButton } from 'owa-fluent-v9-shims';
import FormatStrings from './formatControlStrings.locstring.json';
import { ColorPicker } from '@fluentui/react/lib/ColorPicker';
import { Dialog, DialogFooter } from '@fluentui/react/lib/Dialog';
import { getColorFromString, IColor } from '@fluentui/react/lib/index';
import { useWindow, WindowProvider } from '@fluentui/react/lib/WindowProvider';
import Color from 'color';
import { fixContrast } from 'owa-dark-mode-utilities';
import loc from 'owa-localize';
import { cancelButton } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import * as React from 'react';
import * as ReactDOM from 'owa-react-dom';
import type { ModeIndependentColor } from 'roosterjs-editor-types';
import { getIsDarkTheme } from 'owa-fabric-theme';

const CustomizedColorPickerDialog_Container_Id = 'CustomizedColorPickerDialog';
const BASE_WHITE = '#ffffff';
const DARK_WHITE = '#333333';

const CustomColorPicker = function CustomColorPicker(props: {
    initColor: string;
    onSelect: (color: ModeIndependentColor) => void;
}) {
    const color = React.useRef<Color>(Color(props.initColor));

    const onChange = React.useCallback((ev: any, newColor: IColor) => {
        color.current = Color(newColor.str);
    }, []);

    const onOk = React.useCallback(() => {
        const bgColor = getIsDarkTheme() ? DARK_WHITE : BASE_WHITE;
        const darkColor = fixContrast(
            Color(color.current),
            Color(bgColor),
            true /*useSimpleMethod*/,
            bgColor
        );
        props.onSelect?.({
            lightModeColor: color.current.rgb().toString(),
            darkModeColor: darkColor.rgb().toString(),
        });
        onDismiss();
    }, []);

    const window = useWindow();

    const onDismiss = React.useCallback(() => {
        const div = window?.document.getElementById(CustomizedColorPickerDialog_Container_Id);
        if (div) {
            ReactDOM.unmountComponentAtNode(div);
            div.parentNode?.removeChild(div);
        }
    }, []);

    return (
        <Dialog
            hidden={false}
            dialogContentProps={{
                title: loc(FormatStrings.customColorsText),
            }}
            onDismiss={onDismiss}>
            <ColorPicker
                color={getColorFromString(props.initColor) || 'none'}
                onChange={onChange}
                alphaType={'none'}
                showPreview={true}
            />
            <DialogFooter>
                <FluentButton appearance="primary" onClick={onOk}>
                    {loc(okButton)}
                </FluentButton>
                <FluentButton onClick={onDismiss}>{loc(cancelButton)}</FluentButton>
            </DialogFooter>
        </Dialog>
    );
};

export default function showCustomColorPicker(
    initColor: string,
    onSelect: (color: ModeIndependentColor) => void,
    targetWindow: Window
) {
    targetWindow = targetWindow || window;
    const document = targetWindow.document;
    let div = document.getElementById(CustomizedColorPickerDialog_Container_Id);
    if (!div) {
        div = document.createElement('div');
        div.id = CustomizedColorPickerDialog_Container_Id;
        document.body.appendChild(div);
    }

    ReactDOM.render(
        <>
            <WindowProvider window={targetWindow}>
                <CustomColorPicker initColor={initColor} onSelect={onSelect} />
            </WindowProvider>
        </>,
        div
    );
}
